import { Box, Typography, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import { WidgetTrendChartSkeleton } from '@/components/common/WidgetTrendChartSkeleton';
import { useLocalization } from '@/hooks/useLocalization';
import {
  adaptiveCategoricalTickInterval,
  formatChartDateAxis,
} from '@/utils/charts/axisFormatters';
import { toMonthlyLogins } from './monthlyLogins';
import type { TrendSeries } from '@/models/ServiceTypes';

const CHART_HEIGHT = 280;
const ASSUMED_WIDTH_WHEN_UNMEASURED = 900;
const X_AXIS_HEIGHT = 32;

const TOTAL_SERIES_ID = 'total';
const DEVICE_SERIES_IDS = ['desktop', 'mobile', 'tablet'] as const;

export interface ClientLoginsTrendChartProps {
  series: TrendSeries[] | undefined;
  isLoading: boolean;
  isError: boolean;
  widthPx: number | null;
}

/**
 * Hides a series from the plot while leaving it in the chart's data.
 *
 * The device series exist so the axis tooltip can break a month down, but only
 * the total is drawn: four overlapping lines would say nothing the tooltip does
 * not already say, and three of them would sit at or near zero.
 */
const hiddenSeriesSx = Object.fromEntries(
  DEVICE_SERIES_IDS.flatMap((id) => [
    [`& .MuiLineElement-series-${id}`, { display: 'none' }],
    [`& .MuiAreaElement-series-${id}`, { display: 'none' }],
    [`& .MuiMarkElement-series-${id}`, { display: 'none' }],
    [`& .MuiLineHighlightElement-series-${id}`, { display: 'none' }],
  ]),
);

/**
 * Total client logins by month, with a device breakdown in the tooltip.
 *
 * The x-axis uses a point scale so the months always divide the available width
 * between them: a narrow or half-width widget condenses the axis instead of
 * scrolling sideways (AC 3.8), and the tick labels thin out as the spacing
 * tightens rather than overlapping.
 *
 * The tooltip is the chart's own axis tooltip, so it opens on the month nearest
 * the pointer rather than needing a mark to hit — a month with zero logins
 * still reports (AC 3.3) — and it follows a press-and-slide on touch (AC 3.9).
 */
export const ClientLoginsTrendChart = ({
  series,
  isLoading,
  isError,
  widthPx,
}: ClientLoginsTrendChartProps) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const months = toMonthlyLogins(series);

  if (isLoading) {
    return <WidgetTrendChartSkeleton />;
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: CHART_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant='body2'
          color='error'
        >
          {t('clientActivity.loadError', 'Could not load client data.')}
        </Typography>
      </Box>
    );
  }

  if (months.length === 0) {
    return (
      <Box
        sx={{
          height: CHART_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
        >
          {t('clientActivity.noData', 'No client data available')}
        </Typography>
      </Box>
    );
  }

  const deviceLabels: Record<(typeof DEVICE_SERIES_IDS)[number], string> = {
    desktop: t('clientActivity.desktop', 'Desktop'),
    mobile: t('clientActivity.mobile', 'Mobile'),
    tablet: t('clientActivity.tablet', 'Tablet'),
  };

  return (
    <LineChart
      height={CHART_HEIGHT}
      margin={{ left: 10, right: 20, top: 10, bottom: 0 }}
      xAxis={[
        {
          scaleType: 'point',
          data: months.map((entry) => entry.month),
          valueFormatter: formatChartDateAxis,
          tickLabelInterval: adaptiveCategoricalTickInterval(
            months.length,
            widthPx ?? ASSUMED_WIDTH_WHEN_UNMEASURED,
          ),
          tickLabelStyle: { fontSize: 12 },
          height: X_AXIS_HEIGHT,
        },
      ]}
      series={[
        {
          id: TOTAL_SERIES_ID,
          data: months.map((entry) => entry.total),
          label: t('clientActivity.totalLogins', 'Total Client Logins'),
          showMark: true,
          area: true,
          color: theme.palette.primary.main,
        },
        ...DEVICE_SERIES_IDS.map((id) => ({
          id,
          data: months.map((entry) => entry[id]),
          label: deviceLabels[id],
          showMark: false,
          color: theme.palette.text.secondary,
        })),
      ]}
      hideLegend
      sx={{
        '& .MuiAreaElement-root': { fillOpacity: 0.15 },
        ...hiddenSeriesSx,
      }}
    />
  );
};

export default ClientLoginsTrendChart;
